import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationPicker from "@/components/assessment/LocationPicker";
import {
  createAsset,
  listAssetTypes,
  listValueChainStages,
  updateAsset,
  type Asset,
  type AssetType,
  type ValueChainStage,
} from "@/lib/assets";

const OTHER_ASSET_TYPE = "__other__";

const assetSchema = z
  .object({
    name: z.string().min(1, "Asset name is required"),
    assetType: z.string().min(1, "Select an asset type"),
    customAssetTypeName: z.string().optional(),
    valueChainStageId: z.string().optional(),
    address: z.string().optional(),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    city: z.string().min(1, "City is required"),
    country: z.string().min(1, "Country is required"),
    businessImportance: z.enum(["1", "2", "3", "4"], { required_error: "Select a business importance level" }),
  })
  .refine((v) => v.assetType !== OTHER_ASSET_TYPE || !!v.customAssetTypeName, {
    message: "Enter a name for the custom asset type",
    path: ["customAssetTypeName"],
  })
  .refine((v) => !!v.address || (!!v.latitude && !!v.longitude), {
    message: "Provide an address or both latitude and longitude",
    path: ["address"],
  });

type AssetFormValues = z.infer<typeof assetSchema>;

const BUSINESS_IMPORTANCE_LABELS: Record<string, string> = {
  "1": "Low",
  "2": "Medium",
  "3": "High",
  "4": "Very High",
};

interface AssetFormProps {
  asset?: Asset;
  onSaved: () => void;
  onCancel: () => void;
}

function AssetForm({ asset, onSaved, onCancel }: AssetFormProps) {
  const [assetTypes, setAssetTypes] = useState<AssetType[] | null>(null);
  const [stages, setStages] = useState<ValueChainStage[] | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listAssetTypes(), listValueChainStages()])
      .then(([typesResult, stagesResult]) => {
        if (cancelled) return;
        setAssetTypes(typesResult.assetTypes);
        setStages(stagesResult.stages);
      })
      .catch((err) => {
        if (!cancelled) setServerError(err instanceof Error ? err.message : "Failed to load form options");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const form = useForm<AssetFormValues>({
    resolver: zodResolver(assetSchema),
    defaultValues: {
      name: asset?.name ?? "",
      assetType: asset?.assetTypeId ?? (asset ? OTHER_ASSET_TYPE : ""),
      customAssetTypeName: asset?.customAssetTypeName ?? "",
      valueChainStageId: asset?.valueChainStageId ?? "",
      address: asset?.address ?? "",
      latitude: asset?.latitude ?? "",
      longitude: asset?.longitude ?? "",
      city: asset?.city ?? "",
      country: asset?.country ?? "",
      businessImportance: asset ? (String(asset.businessImportance) as AssetFormValues["businessImportance"]) : undefined,
    },
  });

  const selectedAssetType = form.watch("assetType");
  const selectedType = assetTypes?.find((t) => t.id === selectedAssetType);
  // Needs a manual stage picker when: "Other" was chosen, or the selected type
  // has no value-chain mapping (e.g. Substation) — see spec §3 Section A/B.
  const [watchedLatitude, watchedLongitude] = useWatch({ control: form.control, name: ["latitude", "longitude"] });
  const needsManualStage = selectedAssetType === OTHER_ASSET_TYPE || (!!selectedType && !selectedType.valueChainStageId);

  async function onSubmit(values: AssetFormValues) {
    setServerError(null);

    const input = {
      name: values.name,
      assetTypeId: values.assetType === OTHER_ASSET_TYPE ? null : values.assetType,
      customAssetTypeName: values.assetType === OTHER_ASSET_TYPE ? values.customAssetTypeName! : null,
      valueChainStageId: needsManualStage ? values.valueChainStageId ?? null : null,
      address: values.address || null,
      latitude: values.latitude ? Number(values.latitude) : null,
      longitude: values.longitude ? Number(values.longitude) : null,
      city: values.city,
      country: values.country,
      businessImportance: Number(values.businessImportance) as 1 | 2 | 3 | 4,
    };

    try {
      if (asset) {
        await updateAsset(asset.id, input);
      } else {
        await createAsset(input);
      }
      onSaved();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Failed to save asset");
    }
  }

  if (serverError && (!assetTypes || !stages)) {
    return <p className="text-sm font-medium text-destructive">{serverError}</p>;
  }

  if (!assetTypes || !stages) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Asset name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="assetType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Asset type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an asset type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {assetTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER_ASSET_TYPE}>Other, please specify</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {selectedAssetType === OTHER_ASSET_TYPE && (
          <FormField
            control={form.control}
            name="customAssetTypeName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom asset type name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {needsManualStage && (
          <FormField
            control={form.control}
            name="valueChainStageId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Value chain stage</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a value chain stage" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {stages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => {
            const initialPosition =
              watchedLatitude &&
              watchedLongitude &&
              !Number.isNaN(Number(watchedLatitude)) &&
              !Number.isNaN(Number(watchedLongitude))
                ? { lat: Number(watchedLatitude), lng: Number(watchedLongitude) }
                : null;

            return (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <LocationPicker
                    initialPosition={initialPosition}
                    onLocationSelected={(fields) => {
                      form.setValue("address", fields.address, { shouldValidate: true });
                      form.setValue("latitude", fields.latitude, { shouldValidate: true });
                      form.setValue("longitude", fields.longitude, { shouldValidate: true });
                      if (fields.city) form.setValue("city", fields.city, { shouldValidate: true });
                      if (fields.country) form.setValue("country", fields.country, { shouldValidate: true });
                    }}
                  />
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="latitude"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Latitude</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="longitude"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Longitude</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="businessImportance"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Business importance</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a level" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(["1", "2", "3", "4"] as const).map((level) => (
                    <SelectItem key={level} value={level}>
                      {BUSINESS_IMPORTANCE_LABELS[level]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {serverError && <p className="text-sm font-medium text-destructive">{serverError}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? "Saving..." : "Save asset"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default AssetForm;
